import { state } from './state.js';
import * as config from './config.js';
import { apiUrl, ensureAccessToken } from './api';
import { els } from './ui.js';
import type { CreditBalance } from '../types';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:credit');

const DEBUG = /[?&]debug=1(?!d)/i.test(window.location.search || '');

export type { CreditBalance };

export async function fetchCreditBalance(forceRefresh = false): Promise<CreditBalance | null> {
  const now = Date.now();
  if (
    !forceRefresh &&
    state.creditBalance &&
    now - state.creditLastFetchedAt < config.CREDIT_CACHE_TTL_MS
  ) {
    if (DEBUG) {
      log.debug('Using cached credit balance');
    }
    return state.creditBalance;
  }

  if (state.mode !== 'auth0') {
    if (DEBUG) {
      log.debug('Not fetching credits: mode is', state.mode);
    }
    return null;
  }

  try {
    const token = await ensureAccessToken(false);
    if (!token) {
      if (DEBUG) {
        log.warn('No access token for credit fetch');
      }
      return null;
    }

    const endpoint = apiUrl('user/profile');
    if (DEBUG) {
      log.debug('Fetching credit balance from', endpoint);
    }

    const response = await fetch(endpoint, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    });

    if (!response.ok) {
      log.error('Failed to fetch credit balance:', response.status);
      return null;
    }

    const profile = await response.json();
    const balance: CreditBalance = {
      creditsRemaining: profile.creditsRemaining || 0,
      subscriptionStatus: profile.subscriptionStatus,
      subscriptionExpiresAt: profile.subscriptionExpiresAt,
      lastCreditRenewalAt: profile.lastCreditRenewalAt,
      creditLedger: profile.creditLedger,
    };

    state.creditBalance = balance;
    state.creditLastFetchedAt = now;
    updateHeaderCredits();

    if (DEBUG) {
      log.debug('Credit balance updated:', balance.creditsRemaining);
    }
    return balance;
  } catch (err) {
    log.error('Error fetching credit balance:', err);
    return null;
  }
}

export function clearCreditCache(): void {
  state.creditBalance = null;
  state.creditLastFetchedAt = 0;
  updateHeaderCredits();
}

/**
 * Reflect the cached credit balance in the header pill.
 * Hidden in local mode or before the first successful fetch.
 */
export function updateHeaderCredits(): void {
  const el = els.headerCredits as HTMLElement | null;
  if (!el) return;
  const credits = state.creditBalance?.creditsRemaining;
  if (state.mode !== 'auth0' || typeof credits !== 'number') {
    el.hidden = true;
    return;
  }
  el.textContent = `⚡ ${credits}`;
  el.classList.toggle('header-credits--low', credits <= 0);
  el.hidden = false;
}

export async function displayCreditBalanceInOptions(): Promise<void> {
  if (!els.optionsCreditSection) return;

  if (state.mode !== 'auth0') {
    els.optionsCreditSection.hidden = true;
    return;
  }

  els.optionsCreditSection.hidden = false;

  if (els.optionsCreditLoading) {
    els.optionsCreditLoading.hidden = false;
  }
  if (els.optionsCreditMeta) {
    els.optionsCreditMeta.hidden = true;
  }

  try {
    const balance = await fetchCreditBalance(false);

    if (els.optionsCreditLoading) {
      els.optionsCreditLoading.hidden = true;
    }

    if (!balance) {
      if (els.optionsCreditCount) {
        els.optionsCreditCount.textContent = '—';
      }
      return;
    }

    if (els.optionsCreditCount) {
      els.optionsCreditCount.textContent = String(balance.creditsRemaining || 0);
    }

    if (balance.subscriptionStatus && els.optionsCreditMeta) {
      els.optionsCreditMeta.hidden = false;

      if (els.optionsSubscriptionStatus) {
        const statusBadge = document.createElement('span');
        statusBadge.className =
          'subscription-status-badge ' + (balance.subscriptionStatus || 'inactive');
        let statusText =
          balance.subscriptionStatus === 'active'
            ? 'Active Subscription'
            : balance.subscriptionStatus === 'past_due'
              ? 'Past Due'
              : balance.subscriptionStatus === 'canceled'
                ? 'Canceled'
                : 'Inactive';

        if (balance.subscriptionStatus === 'active' && balance.subscriptionExpiresAt) {
          try {
            const expiresAt = new Date(balance.subscriptionExpiresAt);
            const now = new Date();
            const daysRemaining = Math.ceil(
              (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
            );
            if (daysRemaining > 0) {
              statusText += ` (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left)`;
            } else if (daysRemaining === 0) {
              statusText += ' (expires today)';
            } else {
              statusText = 'Subscription Expired';
            }
          } catch {
            // ignore
          }
        }

        statusBadge.textContent = statusText;
        els.optionsSubscriptionStatus.innerHTML = '';
        els.optionsSubscriptionStatus.appendChild(statusBadge);
      }

      if (balance.lastCreditRenewalAt && els.optionsCreditRenewal && els.optionsRenewalDate) {
        try {
          const renewalDate = new Date(balance.lastCreditRenewalAt);
          const formatted = renewalDate.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          els.optionsRenewalDate.textContent = formatted;
          els.optionsCreditRenewal.hidden = false;
        } catch {
          els.optionsCreditRenewal.hidden = true;
        }
      } else if (els.optionsCreditRenewal) {
        els.optionsCreditRenewal.hidden = true;
      }
    }
  } catch (err) {
    log.error('Error displaying credit balance:', err);
    if (els.optionsCreditLoading) {
      els.optionsCreditLoading.hidden = true;
    }
    if (els.optionsCreditCount) {
      els.optionsCreditCount.textContent = '—';
    }
  }
}
